import createUrlBuilder from 'utilities/url.builder.utils';
import {
  getRequest,
  getImageRequest,
  patchRequest,
  postRequest
} from 'utilities/service.utils';
import AppService from './app.service.context';
import { LodgeEnquiryPayloadType, Params } from 'types';

export const lodgeEnquiryRequest = async (
  requestBody: LodgeEnquiryPayloadType
) => {
  const payload = {
    ...requestBody,
    authorityId:
      requestBody.authorityId === 'other' ||
      requestBody.authorityId === 'private'
        ? null
        : requestBody.authorityId
  };
  const urlBuilder = await createUrlBuilder(`/enquiries`);
  const response = await postRequest(
    AppService,
    urlBuilder.toString(),
    payload
  );
  return response;
};

// GET
export const fetchEnquiryByIdService = async (
  request: string | { enquiryId: string; shareId: string; logo: boolean }
) => {
  const queryParams: any = {};
  let url = '/enquiries/';

  if (typeof request === 'object') {
    const { enquiryId, shareId, logo } = request;
    url += `${enquiryId}`;
    if (logo) {
      queryParams.include = 'LogoImageFile';
    }
    if (shareId) {
      queryParams.shareId = shareId;
    }
  } else {
    url += request;
  }

  const urlBuilder = createUrlBuilder(url);
  const result =
    urlBuilder &&
    (await getRequest(AppService, urlBuilder.toString(), queryParams));
  return result;
};

// UPDATE
export const fetchUpdateEnquiryByIdService = async (requestBody: any) => {
  const { enquiryId, autoRenewUntil } = requestBody;
  const payload = { autoRenewUntil };
  const urlBuilder = createUrlBuilder(`/enquiries/${enquiryId}`);
  const response = await patchRequest(
    AppService,
    urlBuilder.toString(),
    payload
  );
  return response;
};
// GET ALL ENQUIRES
export const fetchAllEnquiriesService = async (queryParams: Params) => {
  const {
    fields = '*',
    include = '',
    offset = 0,
    limit = 20,
    orderBy = 'name',
    filter = ''
  } = queryParams;
  const URL = `/enquiries?fields=${fields}&include=${include}&offset=${offset}&limit=${limit}&returnGeometry=false&order=${orderBy}&filter=${filter}`;
  const urlBuilder = createUrlBuilder(URL);
  const result =
    urlBuilder && (await getRequest(AppService, urlBuilder.toString()));
  return result; // ? result.Enquiries : result;
};

//GET
export const fetchJobDetailsById = async (
  jobId: number,
  enquiryId?: number
) => {
  const urlBuilder = createUrlBuilder(`/system/jobs/${jobId}`);

  if (enquiryId) {
    urlBuilder.addParameter('enquiryId', enquiryId);
  }

  const result =
    urlBuilder && (await getRequest(AppService, urlBuilder.toString()));
  return result;
};

export const fetchDesignActivityService = async () => {
  const urlBuilder = createUrlBuilder(`/system/domains/ENQ_ACT_DESIGN`);
  const result =
    urlBuilder && (await getRequest(AppService, urlBuilder.toString()));
  return result;
};

export const fetchExcavationActivityService = async () => {
  const urlBuilder = createUrlBuilder(`/system/domains/ENQ_ACT_EXCAVATE`);
  const result =
    urlBuilder && (await getRequest(AppService, urlBuilder.toString()));
  return result;
};

// GET
export const fetchEnquiryShareLinkByIdService = async (enquiryId: number) => {
  const urlBuilder = createUrlBuilder(`/enquiries/${enquiryId}/sharelink`);
  const result =
    urlBuilder && (await getRequest(AppService, urlBuilder.toString()));
  return result;
};

export const downloadEnquiryCombinedFile = async (
  fileId: string,
  enquiryId?: number
) => {
  const urlBuilder = createUrlBuilder(`/system/files/${fileId}`);

  urlBuilder.addParameter('format', 'full');

  if (enquiryId) {
    urlBuilder.addParameter('enquiryId', enquiryId);
  }

  return await getRequest(AppService, urlBuilder.toString());
};

export const fetchResponseWithDetail = async ({
  responseId,
  shareId = '',
  enquiryId = ''
}: any) => {
  const url = `/enquiries/response/${responseId}`;

  const urlBuilder = createUrlBuilder(url);

  if (shareId) {
    urlBuilder.addParameter('shareId', shareId);
  }

  if (enquiryId) {
    urlBuilder.addParameter('enquiryId', enquiryId);
  }

  return await getRequest(AppService, urlBuilder.toString());
};

export const getAttachmentPreviewUrl = async (
  fileId: number,
  shareId: string = '',
  enquiryId: string = ''
) => {
  const urlBuilder = createUrlBuilder(
    `/enquiries/response/attachment/${fileId}`
  );

  if (shareId) {
    urlBuilder.addParameter('shareId', shareId);
  }

  if (enquiryId) {
    urlBuilder.addParameter('enquiryId', enquiryId);
  }
  return await getRequest(AppService, urlBuilder.toString());
};

export const getEnquiryConfirmationPDFDownloadUrl = async (
  fileId: string,
  enquiryId?: number
) => {
  const urlBuilder = createUrlBuilder(`/system/files/${fileId}`);
  urlBuilder.addParameter('format', 'url');

  if (enquiryId) {
    urlBuilder.addParameter('enquiryId', enquiryId);
  }

  return await getRequest(AppService, urlBuilder.toString());
};

export const getAdvancedOptions = async (
  enquiryId: number,
  shareId?: string
) => {
  const urlBuilder = createUrlBuilder(`/enquiries/${enquiryId}/attachments`);
  if (shareId) {
    urlBuilder.addParameter('shareId', shareId);
  }
  return await getRequest(AppService, urlBuilder.toString());
};

export const startEnquiryCombinedFileJob = async (
  enquiryId: number,
  fileType: 'zip' | 'pdf',
  shareId?: string,
  includeLookUpAndLive?: boolean,
  lualMapZoomLevel?: number,
  advancedOptions?: any
) => {
  let body: any = {};
  if (shareId) {
    body.shareId = shareId;
  }
  /* 
    Always send includeLookUpAndLive parameter to ensure consistent backend behavior
    This prevents the backend from using cached/default values when the parameter is false
  */
  if (includeLookUpAndLive !== undefined) {
    body.includeLookUpAndLive = includeLookUpAndLive;
  }
  if (lualMapZoomLevel !== undefined) {
    body.lualMapZoomLevel = lualMapZoomLevel;
  }
  if (advancedOptions) {
    body = {
      ...body,
      ...advancedOptions
    };
  }

  const urlBuilder = createUrlBuilder(
    `/enquiries/${enquiryId}/files/download/${fileType}`
  );

  const result =
    urlBuilder && (await postRequest(AppService, urlBuilder.toString(), body));
  return result;
};

export const getResponseBodyThumbnail = async (
  enquiryId: number,
  responseId: number,
  shareId?: string
): Promise<{ thumbnail: string }> => {
  const urlBuilder = createUrlBuilder(
    `/enquiries/${enquiryId}/response/${responseId}/thumbnails/body`
  );

  const params: any = {};
  if (shareId) {
    params.shareId = shareId;
  }

  const base64Image = await getImageRequest(
    AppService,
    urlBuilder.toString(),
    params
  );
  return { thumbnail: base64Image };
};

export const getResponseAttachmentThumbnail = async (
  enquiryId: number,
  responseId: number,
  fileId: number,
  shareId?: string
): Promise<{ thumbnail: string }> => {
  const urlBuilder = createUrlBuilder(
    `/enquiries/${enquiryId}/response/${responseId}/thumbnails/${fileId}`
  );

  const params: any = {};
  if (shareId) {
    params.shareId = shareId;
  }

  const base64Image = await getImageRequest(
    AppService,
    urlBuilder.toString(),
    params
  );
  return { thumbnail: base64Image };
};
